import {
  type InsuredFragment,
  SearchResultType,
  type UniversalSearchQuery,
  useInsuredsQuery,
  useUniversalSearchQuery,
} from "@qw/graphql";
import { useAtom } from "jotai";
import { partition } from "lodash";
import { createContext, useContext, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { pinnedInsuredsAtom } from "../atoms";
import { useKeyboardShortcut } from "../hooks";

type SearchResult = UniversalSearchQuery["search"][number];

interface SearchState {
  insureds: SearchResult[];
  loading: boolean;
  open: boolean;
  pinnedInsureds: InsuredFragment[];
  policies: SearchResult[];
  searchTerm: string;
  term: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

const Search = createContext<SearchState | null>(null);

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [term] = useDebounceValue(searchTerm, 200);
  const [pinnedInsuredIds] = useAtom(pinnedInsuredsAtom);

  const { data: { insureds: pinnedInsureds } = { insureds: [] } } = useInsuredsQuery({
    variables: {
      input: {
        ids: Object.keys(pinnedInsuredIds),
      },
    },
    fetchPolicy: "cache-first",
  });

  const { data: { search } = { search: [] }, loading } = useUniversalSearchQuery({
    variables: { term },
    skip: term.length < 3,
    fetchPolicy: "cache-first",
  });

  const [policies, insureds] = partition(search, ({ type }) => type === SearchResultType.Policy);

  useKeyboardShortcut(["ctrl+k", "command+k"], () => setOpen(true), { disabled: open });

  const value = useMemo(
    () => ({
      insureds,
      loading,
      open,
      pinnedInsureds,
      policies,
      searchTerm,
      term,
      setOpen,
      setSearchTerm,
    }),
    [insureds, loading, open, pinnedInsureds, policies, searchTerm, term]
  );

  return <Search.Provider value={value}>{children}</Search.Provider>;
}

export const useSearch = () => {
  const context = useContext(Search);

  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }

  return context;
};
